import { isObject } from 'formik';
import { isEmpty } from 'lodash';

import {
  ActivityWithChange,
  ActivityWiths,
  AttachmentType,
  NoteAddendum,
  PatientNoteFilterType,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import util from '~/lib/util/util';

import {
  AppointmentTypeName,
  CARE_NAVIGATION_APPOINTMENT_TYPE_NAMES,
  INTAKE_SESSION_APPOINTMENT_TYPE_NAMES,
  THERAPY_DISCHARGE_SESSION_APPOINTMENT_TYPE_NAMES,
  TREATMENT_SESSION_APPOINTMENT_TYPE_NAMES,
  TREATMENT_TEAM_HUDDLE_APPOINTMENT_TYPE_NAMES,
} from '../../constants/scheduling.constants';
import { NoteType } from '../../constants/NoteType';
import { toStateMenuOption } from '../menu';

export const getEmptyNotesContent = (
  key: string,
  isFilterApplied: boolean,
): string => {
  return {
    description: isFilterApplied
      ? "If you didn't find what you're looking for, try to reduce your filters or add something new."
      : 'Click on Add new note to get started.',
    primaryText: isFilterApplied ? 'Oops. Nothing to see here.' : '',
    secondaryText: isFilterApplied
      ? "We couldn't find anything to show you."
      : 'This patient doesn’t have any notes yet.',
  }[key];
};

const APPOINTMENT_TYPE_NOTE_TYPE_BUCKETS: [string[], NoteType][] = [
  [INTAKE_SESSION_APPOINTMENT_TYPE_NAMES, NoteType.INTAKE_SESSION],
  [TREATMENT_SESSION_APPOINTMENT_TYPE_NAMES, NoteType.TREATMENT_SESSION],
  [CARE_NAVIGATION_APPOINTMENT_TYPE_NAMES, NoteType.CARE_NAVIGATION],
  [
    TREATMENT_TEAM_HUDDLE_APPOINTMENT_TYPE_NAMES,
    NoteType.INTERNAL_CARE_COORDINATION,
  ],
  [
    THERAPY_DISCHARGE_SESSION_APPOINTMENT_TYPE_NAMES,
    NoteType.DISCHARGE_SESSION,
  ],
];

export const APPOINTMENT_TYPE_TO_NOTE_TYPE: Record<string, NoteType> =
  APPOINTMENT_TYPE_NOTE_TYPE_BUCKETS.reduce(
    (acc, [appointmentTypes, noteType]) => {
      appointmentTypes.forEach((appointmentType) => {
        acc[appointmentType] = noteType;
      });
      return acc;
    },
    {} as Record<string, NoteType>,
  );

export const getNoteTypeBasedOnAppointmentType = (
  appointmentType: AppointmentTypeName,
): NoteType => {
  if (!appointmentType) return null;
  return APPOINTMENT_TYPE_TO_NOTE_TYPE[appointmentType] ?? null;
};

export const getNotesSelectedFilter = (
  filters: PatientNoteFilterType,
): NoteFilterOptionWithKeyType[] => {
  return Object.keys(filters)?.reduce((optionsList, keyName: string) => {
    const fValue = filters[keyName];
    if (typeof fValue === 'boolean' && fValue) {
      return optionsList.concat({
        displayText: keyName,
        keyName,
        value: fValue,
      });
    }
    if (!isEmpty(fValue) || !util.isEmptyArray(fValue)) {
      if (typeof fValue === 'string') {
        return optionsList.concat({
          displayText: fValue,
          keyName,
          value: fValue,
        });
      }
      if (Array.isArray(fValue)) {
        return optionsList.concat(
          filters[keyName]?.map((fValue) => ({
            displayText: fValue?.primaryText || fValue?.node,
            keyName,
            value: fValue?.value,
          })),
        );
      }
      if (isObject(fValue)) {
        return optionsList.concat({
          displayText: fValue?.node,
          keyName,
          value: fValue?.value,
        });
      }
    }
    return optionsList;
  }, []);
};

export const defaultNotesFilter: PatientNoteFilterType = {
  creator: [],
  creatorRole: [],
  noteType: [],
  sessionLink: null,
  showMyNotes: false,
  showNotesWithAttachment: false,
  status: [
    { node: 'Signed', value: 'signed' },
    { node: 'Draft', value: 'draft' },
  ],
  title: '',
};

export const convertDocumentDataToMenuItem = (
  documentId: string,
  documentName: string,
) => {
  return toStateMenuOption({
    externalId: documentId,
    label: documentName,
  });
};

export const getNotesFilter = (
  documentNavigationState: {
    fromDocumentsTableRow?: boolean;
    documentId?: string;
    documentName?: string;
  } | null,
): PatientNoteFilterType => {
  if (
    documentNavigationState?.fromDocumentsTableRow &&
    documentNavigationState?.documentId
  ) {
    return {
      ...defaultNotesFilter,
      documentName: convertDocumentDataToMenuItem(
        documentNavigationState?.documentId,
        documentNavigationState?.documentName,
      ),
    };
  }

  return defaultNotesFilter;
};

export const computeActivityWithHistory = (
  initial: string[] = [],
  addenda: NoteAddendum[] = [],
): ActivityWithChange[] => {
  const history: ActivityWithChange[] = [];
  let current = [...initial];

  const sortedAddenda = [...addenda].sort((a, b) =>
    a.createdAtDateTime.localeCompare(b.createdAtDateTime),
  );

  for (const {
    createdAtDateTime,
    addedActivityWith = [],
    removedActivityWith = [],
  } of sortedAddenda) {
    const from = [...current];

    current = current.filter((name) => !removedActivityWith.includes(name));

    for (const name of addedActivityWith) {
      if (!current.includes(name)) {
        current.push(name);
      }
    }

    const to = [...current];

    const fromSorted = from.toSorted((a, b) => a.localeCompare(b));
    const toSorted = to.toSorted((a, b) => a.localeCompare(b));

    const changed = JSON.stringify(fromSorted) !== JSON.stringify(toSorted);

    if (
      addedActivityWith.length > 0 ||
      removedActivityWith.length > 0 ||
      changed
    ) {
      history.push({ from, timestamp: createdAtDateTime, to });
    }
  }

  return history;
};

export const getInitialActivityWith = (
  final: ActivityWiths[] = [],
  addenda: NoteAddendum[] = [],
): string[] => {
  let current = final?.map((a) => a.activityWithName);

  [...addenda]
    .sort(
      (a, b) =>
        new Date(b.createdAtDateTime).getTime() -
        new Date(a.createdAtDateTime).getTime(),
    )
    .forEach(({ addedActivityWith = [], removedActivityWith = [] }) => {
      current = current?.filter((name) => !addedActivityWith?.includes(name));
      removedActivityWith?.forEach((name) => {
        if (!current?.includes(name)) current?.push(name);
      });
    });

  return current;
};

export const capitalizeFirstWord = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

export const getSelectValue = (
  fieldName: string,
  values: EditNoteFormData,
  options: NoteMultiSelectOption[],
): EditNoteFormData[keyof EditNoteFormData] => {
  if (fieldName === 'activity' && Array.isArray(values.activity)) {
    return values.activity
      .map((val) => options.find((opt) => opt?.code === val?.code))
      .filter(Boolean);
  }

  return values[fieldName];
};

export const buildAddendumAttachmentsPayload = (
  initialAttachments: AttachmentType[] = [],
  currentAttachments: AttachmentType[] = [],
): AddendumAttachmentChanges => {
  const initialIds = new Set(
    initialAttachments.map((d) => d.attachmentExternalId).filter(Boolean),
  );

  const currentIds = new Set(
    currentAttachments.map((d) => d.attachmentExternalId).filter(Boolean),
  );

  const documentsAdded = currentAttachments
    .filter(
      (d) => d.attachmentExternalId && !initialIds.has(d.attachmentExternalId),
    )
    .map((d) => ({
      documentExternalId: d.attachmentExternalId,
      fileName: d.fileName,
    }));

  const documentsRemoved = initialAttachments
    .filter(
      (d) => d.attachmentExternalId && !currentIds.has(d.attachmentExternalId),
    )
    .map((d) => ({
      documentExternalId: d.attachmentExternalId,
      fileName: d.fileName,
    }));

  return {
    ...(documentsAdded.length && { documentsAdded }),
    ...(documentsRemoved.length && { documentsRemoved }),
  };
};

export const getDocumentChangeText = (
  noteDocumentChange?: AddendumAttachmentChanges,
) => {
  if (!noteDocumentChange) return [];

  const { documentsAdded = [], documentsRemoved = [] } = noteDocumentChange;

  const changes: { files: string[]; action: 'added' | 'removed' }[] = [];

  if (documentsAdded?.length > 0) {
    changes.push({
      files: documentsAdded?.map((doc) => doc?.fileName),
      action: 'added',
    });
  }

  if (documentsRemoved?.length > 0) {
    changes.push({
      files: documentsRemoved?.map((doc) => doc?.fileName),
      action: 'removed',
    });
  }

  return changes;
};
